<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useApi, useToaster, AmbientFabricBackground, UiText, UiButton, UiCard, UiLogo } from "@shopexperts/shared";
import { useAuthStore } from "@/store/auth";
import SuccessCheckmarkIcon from '../../assets/icons/success-chekmark.svg';

const route = useRoute();
const router = useRouter();
const api = useApi();
const toaster = useToaster();
const authStore = useAuthStore();

type VerificationScope = "client" | "expert";

// State
const isLoading = ref(true);
const isSuccess = ref(false);
const errorMessage = ref<string | null>(null);
const newEmail = ref<string | null>(null);
const countdown = ref(5);
const resolvedScope = ref<VerificationScope>("client");
let countdownInterval: ReturnType<typeof setInterval> | null = null;

// Get token and email from query params
const token = computed(() => route.query.token as string | undefined);
const email = computed(() => {
  const emailParam = route.query.email as string | undefined;
  return emailParam ? decodeURIComponent(emailParam) : undefined;
});
const requestedScope = computed<VerificationScope | null>(() => {
  const scope = route.query.scope;
  if (scope === "expert" || scope === "client") {
    return scope;
  }

  if (authStore.user?.role_id === 3) {
    return "expert";
  }

  if (authStore.user?.role_id === 4) {
    return "client";
  }

  return null;
});

function getScopeCandidates(): VerificationScope[] {
  if (requestedScope.value === "expert") {
    return ["expert", "client"];
  }

  if (requestedScope.value === "client") {
    return ["client", "expert"];
  }

  return ["client", "expert"];
}

async function verifyForScope(scope: VerificationScope) {
  const body = {
    token: token.value as string,
    email: email.value as string,
  };

  if (scope === "expert") {
    return api.expert.profile.verifyEmailChange({ body });
  }

  return api.client.profile.verifyEmailChange({ body });
}

// Verify email on mount
onMounted(async () => {
  if (!token.value || !email.value) {
    isLoading.value = false;
    errorMessage.value = "Invalid verification link. Missing token or email.";
    return;
  }

  try {
    let response: { email: string } | null = null;
    let lastError: unknown = null;

    for (const scope of getScopeCandidates()) {
      try {
        response = await verifyForScope(scope);
        resolvedScope.value = scope;
        break;
      } catch (err) {
        lastError = err;
      }
    }

    if (!response) {
      throw lastError;
    }

    isSuccess.value = true;
    newEmail.value = response.email;

    toaster.create({
      title: "Email updated successfully!",
      description: `Your email has been changed to ${response.email}`,
      type: "success",
    });

    // Start countdown for redirect
    startCountdown();
  } catch (err: unknown) {
    isSuccess.value = false;
    
    const error = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Failed to verify email change. The link may be expired or invalid.";
    
    errorMessage.value = message;

    toaster.create({
      title: "Verification failed",
      description: message,
      type: "error",
    });
  } finally {
    isLoading.value = false;
  }
});

function startCountdown() {
  countdownInterval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(countdownInterval!);
      goToDashboard();
    }
  }, 1000);
}

function goToDashboard() {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
  router.push(resolvedScope.value === "expert" ? "/expert/settings" : "/client/settings");
}

// Cleanup on unmount
onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});
</script>

<template>
  <div class="relative min-h-screen overflow-hidden flex items-center justify-center px-4">
    <!-- Ambient Background -->
    <div class="absolute inset-0 w-full h-full z-0">
      <AmbientFabricBackground :settings="{ class: 'w-full h-full' }" />
    </div>

    <UiCard padding="lg" class="relative z-10 w-full max-w-[550px]">
      <div class="flex flex-col items-center justify-center gap-6">
        <!-- Logo -->
        <UiLogo size="md" class="mb-4" />

        <!-- Loading State -->
        <template v-if="isLoading">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-se-primary"></div>
          <div class="flex flex-col gap-2 text-center">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              Verifying your email...
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              Please wait while we verify your email change request.
            </UiText>
          </div>
        </template>

        <!-- Success State -->
        <template v-else-if="isSuccess">
          <SuccessCheckmarkIcon class="w-20 h-20" />
          <div class="flex flex-col gap-2 text-center max-w-sm">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              Email Updated Successfully!
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              Your email has been updated to <span class="font-semibold">{{ newEmail }}</span>. 
              You can now use your new email to log in.
            </UiText>
          </div>
          <UiText as="p" variant="p-small" class="text-se-gray-500">
            Redirecting to settings in {{ countdown }} second{{ countdown !== 1 ? 's' : '' }}...
          </UiText>
          <UiButton
            variant="primary"
            label="Go to Settings"
            @click="goToDashboard"
            class="min-w-[200px]"
          />
        </template>

        <!-- Error State -->
        <template v-else>
          <svg class="w-16 h-16 text-se-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          <div class="flex flex-col gap-2 text-center max-w-sm">
            <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
              Email Verification Failed
            </UiText>
            <UiText as="p" variant="p" class="text-se-gray-600">
              {{ errorMessage }}
            </UiText>
          </div>
          <UiButton
            variant="primary"
            label="Go to Settings"
            @click="goToDashboard"
            class="min-w-[200px]"
          />
          <UiText as="p" variant="p-small" class="text-se-gray-500">
            Or contact support if you continue having issues.
          </UiText>
        </template>
      </div>
    </UiCard>
  </div>
</template>
